import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Facebook, Mail, Phone } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import TikTokIcon from "./TikTokIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", message: "" };
const FACEBOOK_URL =
  "https://www.facebook.com/share/1CVqa1Uwox/?mibextid=wwXIfr";
const TIKTOK_URL = "https://www.tiktok.com/@pratts.hydroseeding";

const inputCls =
  "w-full rounded-sm border border-[#1c4431]/20 bg-white px-4 py-3 text-sm text-[#1a2e22] placeholder:text-[#8a9481] outline-none transition-colors duration-300 focus:border-[#1f5c3d]";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Email sent — Pratt will get back to you shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Couldn't send. Call or text 423-748-2012 instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-[#122e21] bg-[#16382a]"
    >
      <div className="mx-auto grid max-w-7xl gap-16 px-4 py-24 sm:px-8 md:py-32 lg:grid-cols-2 lg:px-16">
        <div>
          <SectionHeading
            overline="06 / Get in touch"
            title="Talk to Pratt."
            testid="contact-heading"
            dark
          />
          <Reveal>
            <a
              data-testid="contact-call-card"
              href="tel:+14237482012"
              className="glow-card group flex items-center gap-6 rounded-md border border-[#1c4431]/15 bg-[#f6f1e3] p-8"
            >
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#1f5c3d] text-[#f6f1e3] shadow-[0_10px_40px_-10px_rgba(28,68,49,0.7)] transition-transform duration-300 group-hover:scale-110">
                <Phone className="h-7 w-7" />
              </span>
              <span>
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#2e7d4f]">
                  <span className="h-2 w-2 animate-pulse-dot rounded-full bg-[#1f5c3d]" />
                  Call or text — talk to the owner
                </span>
                <span className="mt-2 block font-display text-3xl font-extrabold tracking-tight text-[#1a2e22] sm:text-4xl">
                  423-748-2012
                </span>
              </span>
            </a>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                data-testid="contact-text-button"
                href="sms:+14237482012"
                className="rounded-full border border-[#f6f1e3]/30 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-[#f6f1e3] transition-colors duration-300 hover:border-[#9fd8b4] hover:text-[#9fd8b4]"
              >
                Text Us
              </a>
              <a
                data-testid="contact-facebook-link"
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full border border-[#f6f1e3]/30 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-[#f6f1e3] transition-colors duration-300 hover:border-[#9fd8b4] hover:text-[#9fd8b4]"
              >
                <Facebook className="h-4 w-4" />
                Facebook
              </a>
              <a
                data-testid="contact-tiktok-link"
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full border border-[#f6f1e3]/30 px-6 py-3 font-mono text-xs font-bold uppercase tracking-widest text-[#f6f1e3] transition-colors duration-300 hover:border-[#9fd8b4] hover:text-[#9fd8b4]"
              >
                <TikTokIcon className="h-4 w-4" />
                TikTok
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-[#c3d2bd]">
              Call, text, or email for inquiries — whatever's easiest. Pricing
              happens on site: we walk your property, then give you a straight
              number. Serving East Tennessee, Mon–Sat, 7am–7pm.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            data-testid="contact-email-form"
            onSubmit={submit}
            className="rounded-md border border-[#1c4431]/15 bg-[#f6f1e3] p-8"
          >
            <div className="mb-7 flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-[#1c4431]/20 bg-[#e7efe2] text-[#1f5c3d]">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-lg font-bold tracking-tight text-[#1a2e22]">
                  Email Pratt
                </p>
                <p
                  data-testid="contact-email-note"
                  className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5c6b58]"
                >
                  Lands straight at prattshydroseeding@gmail.com
                </p>
              </div>
            </div>
            <div className="grid gap-5">
              <input
                data-testid="contact-name-input"
                required
                placeholder="Full name *"
                value={form.name}
                onChange={set("name")}
                className={inputCls}
              />
              <input
                data-testid="contact-phone-input"
                required
                type="tel"
                placeholder="Phone *"
                value={form.phone}
                onChange={set("phone")}
                className={inputCls}
              />
              <textarea
                data-testid="contact-message-input"
                required
                rows={5}
                placeholder="Tell us about the job — clearing, grading, hydroseeding, pond, timeline... *"
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-send-button"
              type="submit"
              disabled={sending}
              className="mt-7 w-full rounded-full bg-[#1f5c3d] py-4 font-mono text-sm font-bold uppercase tracking-widest text-[#f6f1e3] transition-colors duration-300 hover:bg-[#2a7452] disabled:opacity-60"
            >
              {sending ? "Sending..." : "Send Email"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
